import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { BITBUCKET_API_URL, BITBUCKET_STATE_API_URL } from '../../utils/apiEndpoints';
import clientService from '../../apiClient';
import { Notification } from '../../components/Notification';

const initialState = {
  loading: false,
  bitbucketWorkspaces: [],
  bitbucketRepositories: {
    fetched: false,
    loading: false,
    page: 0,
    limit: 0,
    totalSize: 0,
    data: [],
  },
  bitbucketBranches: {
    fetched: false,
    loading: false,
    page: 0,
    limit: 0,
    totalSize: 0,
    data: [],
  },
  bitbucketFolders: {
    fetched: false,
    loading: false,
    page: 0,
    limit: 0,
    totalSize: 0,
    data: [],
  },
  bitbucket: {
    loading: false,
    user: null,
    error: null,
  },
};

const bitbucketSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchBitbucketWorkspaces.pending, (state) => {
      state.loading = true;
    }),
      builder.addCase(fetchBitbucketWorkspaces.fulfilled, (state, action) => {
        state.loading = false;
        state.bitbucketWorkspaces = Array.isArray(action.payload.data) ? action?.payload?.data : [];
        state.bitbucketRepositories = { page: 0, totalSize: 0, limit: 0, data: [] };
        state.bitbucketBranches = { limit: 0, totalSize: 0, page: 0, data: [] };
        state.bitbucketFolders = { limit: 0, totalSize: 0, page: 0, data: [] };
      }),
      builder.addCase(fetchBitbucketWorkspaces.rejected, (state) => {
        state.loading = false;
        Notification.error('Error', 'Request failed');
      }),
      builder.addCase(fetchBitbucketRepositories.pending, (state) => {
        state.bitbucketRepositories.loading = true;
      }),
      builder.addCase(fetchBitbucketRepositories.fulfilled, (state, action) => {
        state.bitbucketRepositories.loading = false;
        state.bitbucketRepositories.totalSize = action?.payload?.totalSize;
        state.bitbucketRepositories.limit = action?.payload?.limit;
        state.bitbucketRepositories.page = action?.payload?.page;
        state.bitbucketRepositories.data = [...state.bitbucketRepositories.data, ...(action?.payload?.data || [])];
        if (!state.bitbucketRepositories.fetched) state.bitbucketRepositories.fetched = true;
        state.bitbucketBranches = { limit: 0, totalSize: 0, page: 0, data: [] };
        state.bitbucketFolders = { limit: 0, totalSize: 0, page: 0, data: [] };
      }),
      builder.addCase(fetchBitbucketRepositories.rejected, (state) => {
        state.bitbucket.loading = false;
        Notification.error('Error', 'Request failed');
      }),
      builder.addCase(fetchBitbucketBranches.pending, (state) => {
        state.bitbucketBranches.loading = true;
      }),
      builder.addCase(fetchBitbucketBranches.fulfilled, (state, action) => {
        state.bitbucketBranches.loading = false;
        state.bitbucketBranches.totalSize = action?.payload?.totalSize;
        state.bitbucketBranches.limit = action?.payload?.limit;
        state.bitbucketBranches.page = action?.payload?.page;
        state.bitbucketBranches.data = [...state.bitbucketBranches.data, ...(action?.payload?.data || [])];
        if (!state.bitbucketBranches.fetched) state.bitbucketBranches.fetched = true;
        state.bitbucketFolders = { limit: 0, totalSize: 0, page: 0, data: [] };
      }),
      builder.addCase(fetchBitbucketBranches.rejected, (state) => {
        state.bitbucketBranches.loading = false;
        Notification.error('Error', 'Request failed');
      }),
      builder.addCase(fetchBitbucketFolders.pending, (state) => {
        state.bitbucket.loading = true;
      }),
      builder.addCase(fetchBitbucketFolders.fulfilled, (state, action) => {
        state.bitbucketFolders.loading = false;
        state.bitbucketFolders.totalSize = action?.payload?.totalSize;
        state.bitbucketFolders.limit = action?.payload?.limit;
        state.bitbucketFolders.page = action?.payload?.page;
        state.bitbucketFolders.data = [...state.bitbucketFolders.data, ...(action?.payload?.data || [])];
        if (!state.bitbucketFolders.fetched) state.bitbucketFolders.fetched = true;
      }),
      builder.addCase(fetchBitbucketFolders.rejected, (state) => {
        state.bitbucketFolders.loading = false;
        Notification.error('Error', 'Request failed');
      }),
      builder.addCase(fetchBitbucketAuthorization.pending, (state) => {
        state.bitbucket.loading = true;
      }),
      builder.addCase(fetchBitbucketAuthorization.fulfilled, (state, action) => {
        state.bitbucket.user = action?.payload?.data;
        state.bitbucket.loading = false;
        state.bitbucket.error = null;
      }),
      builder.addCase(fetchBitbucketAuthorization.rejected, (state, action) => {
        state.bitbucket.user = null;
        state.bitbucketWorkspaces = [];
        state.bitbucketRepositories = [];
        state.bitbucketFolders = [];
        state.bitbucket.error = 'authentication error';
        state.bitbucket.loading = false;
      });
  },
});

export const sendBitbucketStateToServer = createAsyncThunk(
  'bitbucket/sendBitbucketStateToServer',
  async (params, { dispatch }) => {
    const response = await clientService.get(`${BITBUCKET_STATE_API_URL}`, {
      params,
    });
    return response;
  }
);

export const fetchBitbucketWorkspaces = createAsyncThunk('bitbucket/fetchBitbucketWorkspaces', async () => {
  const response = await clientService.get(`${BITBUCKET_API_URL}/workspaces`);
  return response;
});

export const fetchBitbucketRepositories = createAsyncThunk('bitbucket/fetchBitbucketRepositories', async (params) => {
  const response = await clientService.get(`${BITBUCKET_API_URL}/repositories`, { params });
  return {
    data: response?.data?.values || [],
    limit: response?.data?.pagelen,
    totalSize: response?.data?.size,
    page: response?.data?.page,
  };
});

export const fetchBitbucketBranches = createAsyncThunk('bitbucket/fetchBitbucketBranches', async (params) => {
  const response = await clientService.get(`${BITBUCKET_API_URL}/branches`, { params });
  return {
    data: response?.data?.values || [],
    limit: response?.data?.pagelen,
    totalSize: response?.data?.size,
    page: response?.data?.page,
  };
});

export const fetchBitbucketFolders = createAsyncThunk('bitbucket/fetchBitbucketFolders', async (params) => {
  const response = await clientService.get(`${BITBUCKET_API_URL}/files`, {
    params,
  });
  return {
    data: response?.data?.values || [],
    limit: response?.data?.pagelen,
    totalSize: response?.data?.size,
    page: response?.data?.page,
  };
});

export const fetchBitbucketAuthorization = createAsyncThunk('bitbucket/fetchBitbucketAuthorization', async () => {
  const response = await clientService.get(`${BITBUCKET_API_URL}/user`);
  return response;
});

export default bitbucketSlice.reducer;
